import { Line } from './Line';
import { Scaler } from './Scaler';
import { ConnectionPoint } from './ConnectionPoint';
import { Selectable } from './Selectable';
import { ICloneable, IColorChangeable, IObject3D } from '../Interfaces';

declare const THREE: any;

export interface PolyLineRecord {
  currentPoint: ConnectionPoint;
  currentLine: Line | undefined;
  prevLine: Line | undefined;
}

class PolylineCloner {

  private static cloneChildren(records: PolyLineRecord[]): IObject3D[] {
    const result = [];

    let prevLineId, currentLineId;
    for (const record of records) {
      currentLineId = undefined;
      if (record.currentLine) {
        const line = new Line();
        line.position.copy(record.currentLine.position);
        currentLineId = line.id;
        result.push(line);
      }
      const point = new ConnectionPoint(prevLineId, currentLineId);
      point.position.copy(record.currentPoint.position);
      result.push(point);
      prevLineId = currentLineId;
    }

    return result;
  }

  static clone(polyline: PolyLine, color): IObject3D {
    const result = new PolyLine();
    const children = PolylineCloner.cloneChildren(polyline.objectsList());
    result.add(...children);
    result.updateConnection();
    result.setColor(color);
    return result;
  }

}

export class PolyLine extends Selectable implements IColorChangeable, ICloneable {

  readonly scaler: Scaler;
  private color;
  static readonly Name = 'PolyLine';

  constructor() {
    super();
    this.scaler = new Scaler();
    this.color = new THREE.Color(0x000000);
    this.add(this.scaler);
    this.name = PolyLine.Name;
  }

  objectsList(): Array<PolyLineRecord> {
    const cpoints = this.children.filter(ch => ch instanceof ConnectionPoint) as Array<ConnectionPoint>;
    let point = cpoints.find(p => p.prevLineId === undefined);
    const result = [];
    while (point !== undefined) {
      result.push({
        currentPoint: point,
        prevLine: this.getObjectById(point.prevLineId),
        currentLine: this.getObjectById(point.currentLineId)
      });
      point = cpoints.find(p => p.prevLineId === point.currentLineId && point.currentLineId !== undefined);
    }
    return result;
  }

  updateConnection() {
    const list = this.objectsList();
    for (let i = 0; i < list.length - 1; i++) {
      const first = list[i];
      const second = list[i + 1];
      const firstPointPosition = first.currentPoint.position;
      const secondPointPosition = second.currentPoint.position;
      const currentLine = first.currentLine;
      currentLine.position.copy(firstPointPosition);
      currentLine.update(firstPointPosition, secondPointPosition);
    }
    this.selection.update();
    this.scaler.update();
  }

  getBoundingBoxObjects() {
    return this.children.filter(child => child instanceof Line);
  }

  setColor(color): void {
    this.color.copy(color);
    this.children.filter(child => child instanceof Line)
      .forEach(line => line.setColor(color));
  }

  highlight() {
    super.highlight();
    this.scaler.update();
    this.scaler.visible = true;
  }

  unhighlight() {
    super.unhighlight();
    this.scaler.visible = false;
    this.children.filter(ch => ch instanceof ConnectionPoint).forEach(ch => {
      ch.visible = false;
    });
  }

  duplicate(): IObject3D {
    return PolylineCloner.clone(this, this.color);
  }

  getInfo() {
    const records = this.objectsList();
    const points = [];
    records.forEach(rec => {
      let pos = rec.currentPoint.position;
      points.push({ x: pos.x, y: pos.y, z: pos.z });
    });
    let pos = {
      x: this.position.x,
      y: this.position.y,
      z: this.position.z
    };
    return {
      type: this.name,
      position: pos,
      points: points,
      color: this.color.getHex()
    }
  }

  rebuild(info) {
    this.position.set(info.position.x, info.position.y, info.position.z);
    let prevLine = undefined;
    let currentLine = undefined;
    let connectionPoint;
    let prevLineId;
    for (let i = 0; i < info.points.length - 1; i++) {
      currentLine = new Line();
      let pos = info.points[i];
      let nextPos = info.points[i + 1];
      this.add(currentLine);
      currentLine.position.set(pos.x, pos.y, pos.z);
      if (prevLine) {
        prevLine.update(prevLine.position, currentLine.position);
      }
      prevLineId = prevLine ? prevLine.id : undefined;

      connectionPoint = new ConnectionPoint(prevLineId, currentLine.id);
      this.add(connectionPoint);
      connectionPoint.position.copy(currentLine.position);

      prevLine = currentLine;
    }
    prevLineId = prevLine ? prevLine.id : undefined;
    connectionPoint = new ConnectionPoint(prevLineId, undefined);
    let pos = info.points[info.points.length - 1];
    this.add(connectionPoint);
    connectionPoint.position.set(pos.x, pos.y, pos.z);
    prevLine.update(prevLine.position, connectionPoint.position);

    let color = new THREE.Color(info.color);
    this.setColor(color);
  }

}



// WEBPACK FOOTER //
// ./src/app/services/proTools/object3D/PolyLine.ts